import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { DatePicker } from "@admin/components/molecules/Picker/DatePicker";
import { SupplierSelection } from "@admin/components/molecules/SelectWithAdd/SupplierSelection";
import {
  AddFoodDrawal,
  addFoodDrawal,
  dateToISO,
  FoodDrawal,
  InstanceStatus,
  updateFoodDrawal,
  useAuthStore,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { SupplierModalAction } from "../MyLists/SuppliersTable";
import { UpsertSupplierModal } from "./UpsertSupplierModal";

interface ProductReturnModalProps extends ModalProps {
  title: string;
  companyId?: string;
  companyLocationId?: string;
  initialData?: FoodDrawal;
}

export function ProductReturnModal({
  title,
  companyId,
  companyLocationId,
  initialData,
  ...props
}: ProductReturnModalProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const addFoodDrawalMutation = useMutation({ mutationFn: addFoodDrawal });

  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: updateFoodDrawal,
  });

  const foodDrawalSchema = z.object({
    supplierId: z.string().min(1, t("food.Supplier is Required")),
    expirtyDate: z.string().min(1, t("food.Expiry Date is required")),
    foodDrawalDate: z.string().min(1, t("food.Food drawal date is required")),
    quantityReceivedDate: z
      .string()
      .min(1, t("food.Quantity receive date is required")),
    isUsed: z.string().min(1, t("food.Used info is required")),
    correctiveAction: z
      .string()
      .min(1, t("food.Corrective action is required")),
    foodName: z.string().min(1, t("food.Product is required")),
  });

  type FoodDrawalSchema = z.infer<typeof foodDrawalSchema>;
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    reset,
  } = useForm<FoodDrawalSchema>({
    resolver: zodResolver(foodDrawalSchema),
  });

  const handleSubmitClick = handleSubmit((data) => {
    const drawalData = {
      ...data,
      companyId: companyId,
      companyLocationId: companyLocationId,
      registratedUser: user?.id,
      expirtyDate: dateToISO(new Date(data.expirtyDate)),
      foodDrawalDate: dateToISO(new Date(data.foodDrawalDate)),
      file: "",
      status: InstanceStatus.completed,
    } as AddFoodDrawal;

    if (initialData) {
      updateMutation.mutate(
        {
          id: initialData.id,
          status: InstanceStatus.completed,
          ...drawalData,
        },
        {
          onSuccess: () => {
            toast.success(t("food.Product return added succesfully"));
            queryClient.invalidateQueries({
              queryKey: ["locationFoodDrawals", companyLocationId],
            });
            reset();
            props?.onClose?.();
          },
          onError: () => {
            toast.error(
              t("common.Failed to add item", {
                item: t("food.Product Return"),
              }),
            );
          },
        },
      );

      return;
    }

    addFoodDrawalMutation.mutate(drawalData, {
      onSuccess: () => {
        toast.success(t("food.Product return added succesfully"));
        queryClient.invalidateQueries({
          queryKey: ["locationFoodDrawals", companyLocationId],
        });
        reset();
        props?.onClose?.();
      },
      onError: () => {
        toast.error(
          t("common.Failed to add item", {
            item: t("food.Product Return"),
          }),
        );
      },
    });
  });

  const handleClose = () => {
    reset();
    props.onClose?.();
  };

  useEffect(() => {
    if (initialData) {
      reset({
        supplierId: initialData.supplierId,
        expirtyDate: initialData.expirtyDate,
        foodDrawalDate: initialData.foodDrawalDate,
        quantityReceivedDate: initialData.quantityReceivedDate,
        isUsed: initialData.isUsed,
        correctiveAction: initialData.correctiveAction,
        foodName: initialData.foodName,
      });
    }
  }, [initialData, reset]);

  return (
    <>
      <Modal
        {...props}
        actionButtons={
          <>
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={addFoodDrawalMutation.isPending}
            >
              {t("common.Discard")}
            </Button>
            <Button
              onClick={handleSubmitClick}
              loading={addFoodDrawalMutation.isPending}
            >
              {t("food.Submit Entry")}
            </Button>
          </>
        }
        title={title}
      >
        <form>
          <Label title={t("food.Supplier")} required>
            <div className="flex flex-col items-start w-full gap-2">
              <SupplierSelection
                companyId={companyId}
                onAddNew={() => setIsAddSupplierModalOpen(true)}
                onChange={(e) => setValue("supplierId", e.target.value)}
                defaultValue={initialData?.supplierId}
              />
              <ErrorMessage message={errors.supplierId?.message} />
            </div>
          </Label>
          <Label title={t("food.Name of the Product")} required>
            <div className="flex flex-col items-start w-full gap-2">
              <Input
                {...register("foodName")}
                name="foodName"
                type="text"
                placeholder={t("food.Name of the Product")}
              />
              <ErrorMessage message={errors.foodName?.message} />
            </div>
          </Label>
          <Label title={t("food.Expiration Date")} required>
            <div className="flex flex-col items-start w-full gap-2">
              <Controller
                name="expirtyDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? new Date(field.value) : new Date()}
                    onChange={(date: Date) => field.onChange(dateToISO(date))}
                  />
                )}
              />
              <ErrorMessage message={errors.expirtyDate?.message} />
            </div>
          </Label>
          <Label title={t("food.Date of Withdrawal")} required>
            <div className="flex flex-col items-start w-full gap-2">
              <Controller
                name="foodDrawalDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? new Date(field.value) : new Date()}
                    onChange={(date: Date) => field.onChange(dateToISO(date))}
                  />
                )}
              />
              <ErrorMessage message={errors.foodDrawalDate?.message} />
            </div>
          </Label>
          <Label title={t("food.Quantity Bought")} required>
            <div className="flex flex-col items-start w-full gap-2">
              <Input
                {...register("quantityReceivedDate")}
                name="quantityReceivedDate"
                type="number"
                placeholder={t("food.Quantity Bought")}
              />
              <ErrorMessage message={errors.quantityReceivedDate?.message} />
            </div>
          </Label>
          <Label title={t("food.Has product been used")} required>
            <div className="flex flex-col items-start w-full gap-2">
              <Select
                {...register("isUsed")}
                name="isUsed"
                variant="standart"
                options={[
                  { label: t("common.Yes"), value: t("common.Yes") },
                  { label: t("common.No"), value: t("common.No") },
                ]}
                defaultValue={initialData?.isUsed}
                placeholder={t("common.Select an option")}
              />
              <ErrorMessage message={errors.isUsed?.message} />
            </div>
          </Label>
          <Label title={t("food.Corrective Action")} required>
            <div className="flex flex-col items-start w-full gap-2">
              <Input
                {...register("correctiveAction")}
                name="correctiveAction"
                type="text"
                placeholder={t("food.Corrective Action")}
              />
              <ErrorMessage message={errors.correctiveAction?.message} />
            </div>
          </Label>
        </form>
      </Modal>
      <UpsertSupplierModal
        open={isAddSupplierModalOpen}
        onClose={() => setIsAddSupplierModalOpen(false)}
        companyId={companyId}
        type={SupplierModalAction.ADD}
      />
    </>
  );
}
